/**
 * Safe localStorage operations with error handling
 * Prevents crashes in private browsing mode or when storage is full
 */

import { envStorage, sessionEnvStorage } from "./envIsolatedStorage";

// Export environment-isolated storage (recommended for all new code)
export { createScopedStorage, envStorage } from "./envIsolatedStorage";

/**
 * Safely get an item from localStorage with environment isolation
 * @param key - The storage key
 * @returns The stored value or null if not found or error occurred
 */
export const safeGet = (key: string): string | null => {
  return envStorage.getItem(key);
};

/**
 * Safely set an item in localStorage with environment isolation
 * @param key - The storage key
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export const safeSet = (key: string, value: string): boolean => {
  return envStorage.setItem(key, value);
};

/**
 * Safely remove an item from localStorage with environment isolation
 * @param key - The storage key
 * @returns true if successful, false otherwise
 */
export const safeRemove = (key: string): boolean => {
  return envStorage.removeItem(key);
};

/**
 * Safely get and parse JSON from localStorage with environment isolation
 * @param key - The storage key
 * @returns The parsed object or null if not found, invalid, or error occurred
 */
export const safeGetJSON = <T = unknown>(key: string): T | null => {
  return envStorage.getJSON<T>(key);
};

/**
 * Safely set JSON in localStorage with environment isolation
 * @param key - The storage key
 * @param value - The value to stringify and store
 * @returns true if successful, false otherwise
 */
export const safeSetJSON = <T = unknown>(key: string, value: T): boolean => {
  return envStorage.setJSON(key, value);
};

/**
 * Safely remove multiple items from localStorage
 * @param keys - Array of storage keys to remove
 */
export const safeRemoveMultiple = (keys: string[]): void => {
  for (const key of keys) {
    safeRemove(key);
  }
};

/** Storage key of the PKCE code verifier used by the TPass token exchange. */
export const CODE_VERIFIER_KEY = "code_verifier";

/**
 * Safe, environment-isolated sessionStorage operations.
 * Use for short-lived auth artefacts (e.g. the PKCE code verifier) that must not outlive the tab.
 * This assumes every auth redirect (TPass and Cognito) returns to the same tab, which keeps its
 * sessionStorage; a flow that completes in a new tab or window would not find the value.
 */
export const sessionStore = {
  safeGet: (key: string): string | null => sessionEnvStorage.getItem(key),
  safeSet: (key: string, value: string): boolean => sessionEnvStorage.setItem(key, value),
  safeRemove: (key: string): boolean => sessionEnvStorage.removeItem(key),
} as const;
